'use client';

import { useRouter } from '@bprogress/next/app';
import Link from 'next/link';
import { useTranslations } from 'next-intl';
import { useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { VariantProps } from 'tailwind-variants';

import { AUTH_MAX_AGE } from '@/fo/api/magento';
import { createGetFieldPropsWithTracking } from '@/fo/feature/auth/utils';
import ArrowRightIcon from '@/fo/icons/base/icons/arrow.svg';
import { UiAuthInputCode } from '@/fo/ui/auth/input/code';
import { UiKitButtonBack } from '@/fo/ui/kit/button/back';
import { UiKitButtonBase } from '@/fo/ui/kit/button/base';
import { UiKitInputBase, UiKitInputBaseProps } from '@/fo/ui/kit/input/base';
import { UiKitInputPassword } from '@/fo/ui/kit/input/password';
import { FormMessage, useForm } from '@/fo/ui/mod/form/base';
import { utilEventAuthModal } from '@/fo/util/event-auth-modal';
import {
  phoneMask,
  utilFormatPhoneNumber
} from '@/fo/util/format-phone-number';

import { loginByPhone, sendAuthCode } from '../api/auth';

import { styles } from './FeatureAuthFormLogin.styles';
import { formAction } from './formAction';

type Variants = VariantProps<typeof styles>;
type NativeElement = React.HTMLAttributes<HTMLDivElement>;

export interface FeatureAuthFormLoginProps {
  color?: Variants['color'];
  onClick?: NativeElement['onClick'];
  /** Передаётся из UiAuthModalBase при возврате из «Забыли пароль» */
  showInformer?: boolean;
  forgotPasswordEmail?: string;
}

export function FeatureAuthFormLogin({
  color,
  showInformer,
  forgotPasswordEmail,
  ...props
}: FeatureAuthFormLoginProps) {
  const slots = styles({
    color,
    informerState: showInformer && forgotPasswordEmail ? 'visible' : 'hidden'
  });
  const t = useTranslations();
  const router = useRouter();

  const { state, pending, action, getFieldProps } = useForm(
    formAction,
    {},
    {
      displayMessageInToast: false
    }
  );
  const [switchContact, setSwitchContact] = useState<'phone' | 'email'>(
    'email'
  );
  const [formStep, setFormStep] = useState<'login' | 'authCode'>('login');
  const [loginValue, setLoginValue] = useState('');
  const [passwordValue, setPasswordValue] = useState('');
  const [messageHidden, setMessageHidden] = useState(false);
  const [codeMessage, setCodeMessage] = useState<string | null>(null);
  const [codeMessageStatus, setCodeMessageStatus] = useState<
    'error' | 'success' | null
  >(null);
  const [codeMessageHidden, setCodeMessageHidden] = useState(false);
  const loginInputRef = useRef<HTMLInputElement>(null);
  const switchContactProps = getFieldProps(switchContact);

  useEffect(() => {
    if (!pending && state?.message) setMessageHidden(false);
  }, [pending, state?.message]);

  const getFieldPropsWithTracking = createGetFieldPropsWithTracking(
    getFieldProps,
    (name, value) => {
      if (name === 'password') setPasswordValue(String(value));
    }
  );

  const isSubmitEnabled = Boolean(loginValue?.trim()) && Boolean(passwordValue);

  const beforeMaskedPhoneInputChange: UiKitInputBaseProps['beforeMaskedStateChange'] =
    (state) => {
      if (!state?.previousState?.value && state?.nextState?.value === '8') {
        return { ...state.nextState, value: '+7 (' };
      }

      if (
        state?.nextState?.value === '+7 (' &&
        state?.previousState?.value === '+7 ('
      ) {
        return { ...state.nextState, value: '' };
      }

      if (switchContact === 'email') {
        return { ...state.nextState, value: state.nextState.value || '' };
      }

      if (
        state?.nextState?.value &&
        !/^[0-9 ()+-]+$/.test(state.nextState.value)
      ) {
        flushSync(() => setSwitchContact('email'));
        return { ...state.nextState, value: state.currentState?.value || '' };
      }

      return { ...state.nextState };
    };

  const onSwitchContactChange = (value: string) => {
    if (value === '8') {
      value = '+7 (';
      setSwitchContact('phone');
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const isEmailFormat = emailRegex.test(value);

    const startsWithPlus7 = value.startsWith('+7 (');
    const minPhoneLength = 6;
    const isPhoneChars = /^[\d ()+-]+$/.test(value);

    if (isEmailFormat) {
      setSwitchContact('email');
    } else if (
      startsWithPlus7 ||
      (value.length >= minPhoneLength && isPhoneChars)
    ) {
      setSwitchContact('phone');
    } else {
      setSwitchContact('email');
    }

    setLoginValue(value);
    switchContactProps.onChange?.(value);
  };

  const onSuccess = () => {
    router.refresh();
    utilEventAuthModal.close();
  };

  useEffect(() => {
    if (state.data) {
      const login = state.data.phone ?? state.data.email ?? '';
      if (login) setLoginValue(String(login));
      if (state.data.password != null)
        setPasswordValue(String(state.data.password));
    }
  }, [state.data]);

  useEffect(() => {
    loginInputRef.current?.focus();
  }, [switchContact]);

  return (
    <div
      className={slots.base()}
      {...props}
    >
      <h2 className={slots.title()}>{t('auth.titleAuth')}</h2>
      {formStep === 'login' && (
        <>
          <div className={slots.subtitle()}>
            <p>{t('auth.subtitle')}</p>
            <p>
              {t.rich('auth.createProfile', {
                link: (chunks) => (
                  <Link
                    href="#"
                    className={slots.informer()}
                    onClick={(e) => {
                      e.preventDefault();
                      utilEventAuthModal.switchToRegistrationTab();
                    }}
                  >
                    {chunks}
                  </Link>
                )
              })}
            </p>
          </div>
          <form
            action={action}
            className={slots.form()}
          >
            <div className={slots.fields()}>
              <div>
                <UiKitInputBase
                  {...switchContactProps}
                  mask={switchContact === 'phone' ? phoneMask : ''}
                  onChange={onSwitchContactChange}
                  ref={loginInputRef}
                  beforeMaskedStateChange={beforeMaskedPhoneInputChange}
                  type={switchContact === 'phone' ? 'tel' : 'text'}
                  label={t('form.label.emailOrPhone')}
                  value={loginValue}
                  disabled={pending}
                />
                {switchContact === 'phone' &&
                  utilFormatPhoneNumber(loginValue).length ===
                    phoneMask.length && (
                    <UiKitButtonBase
                      color="white"
                      className={slots.loginBySMS()}
                      onClick={async () => {
                        setFormStep('authCode');

                        const response = await sendAuthCode(loginValue);

                        if (response.error) {
                          setCodeMessage(response.error);
                          setCodeMessageStatus('error');
                          setCodeMessageHidden(false);
                        }
                      }}
                    >
                      <span>{t('form.button.loginBySMS')}</span>
                      <span className={slots.iconWrapper()}>
                        <ArrowRightIcon className={slots.arrowIcon()} />
                      </span>
                    </UiKitButtonBase>
                  )}
              </div>
              <UiKitInputPassword
                label={t('form.label.password')}
                {...getFieldPropsWithTracking('password')}
                disabled={pending}
              />
            </div>
            <UiKitButtonBase
              type="submit"
              className={slots.button()}
              loading={pending}
              color="secondary"
              disabled={!isSubmitEnabled}
            >
              <span>{t('form.button.login')}</span>
            </UiKitButtonBase>
            <p
              className={slots.forgotPassword()}
              onClick={() => utilEventAuthModal.showForgotPassword()}
            >
              {t('auth.forgotPassword')}
            </p>

            {!pending && state?.message && state?.status && !messageHidden && (
              <FormMessage
                message={state.message}
                status={state.status}
                duration={3000}
                onDismiss={() => {
                  setMessageHidden(true);
                  if (state?.status === 'success') onSuccess();
                }}
              />
            )}
          </form>
        </>
      )}
      {formStep === 'authCode' && (
        <div>
          <div className={slots.backButtonWrapper()}>
            <UiKitButtonBack
              onClick={() => setFormStep('login')}
              className={slots.backButton()}
            />
          </div>
          <UiAuthInputCode
            phoneNumber={loginValue}
            onSendCode={async (code) => {
              const response = await loginByPhone(
                loginValue,
                code,
                state.data?.rememberMe ? { maxAge: AUTH_MAX_AGE } : undefined
              );

              if (response.status === 'success') {
                setCodeMessage(
                  response.message ?? t('form.message.authSuccess')
                );
                setCodeMessageStatus('success');
                setCodeMessageHidden(false);
              }

              return response;
            }}
            onMessage={(msg, status) => {
              setCodeMessage(msg);
              setCodeMessageStatus(status);
              setCodeMessageHidden(false);
            }}
            onResend={async () => {
              const response = await sendAuthCode(loginValue);

              if (response.error) {
                setCodeMessage(response.error);
                setCodeMessageStatus('error');
                setCodeMessageHidden(false);
              }
            }}
          />

          {codeMessage && codeMessageStatus && !codeMessageHidden && (
            <FormMessage
              message={codeMessage}
              status={codeMessageStatus}
              duration={3000}
              onDismiss={() => {
                setCodeMessageHidden(true);
                if (codeMessageStatus === 'success') onSuccess();
              }}
            />
          )}
        </div>
      )}
    </div>
  );
}
